import type { ReactNode } from 'react';
import '../styles/tokens.scss';
import './styles.scss';

export interface IUptimeFilterGroupProps {
    label: ReactNode;
    children: ReactNode;
    className?: string;
}

export const UptimeFilterGroup = ({ label, children, className }: IUptimeFilterGroupProps) => (
    <div className={`upt-fgroup${className ? ` ${className}` : ''}`}>
        <span className='upt-fgroup__label'>{label}</span>
        {children}
    </div>
);

export interface IUptimeFilterPillsProps<T extends string | number> {
    options: { value: T; label: ReactNode }[];
    isSelected: (value: T) => boolean;
    onToggle: (value: T) => void;
}

export const UptimeFilterPills = <T extends string | number>({
    options,
    isSelected,
    onToggle,
}: IUptimeFilterPillsProps<T>) => (
    <div className='upt-pills'>
        {options.map(option => (
            <button
                type='button'
                key={option.value}
                className='upt-pillbtn'
                aria-pressed={isSelected(option.value)}
                onClick={() => onToggle(option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
);

export interface IUptimeChecklistItem {
    key: string;
    label: ReactNode;
    count?: ReactNode;
}

export interface IUptimeChecklistProps {
    query: string;
    onQueryChange: (query: string) => void;
    placeholder: string;
    items: IUptimeChecklistItem[];
    selected: string[];
    onToggle: (key: string) => void;
    emptyText: ReactNode;
    hint?: ReactNode;
    maxHeight?: number;
    clearLabel?: ReactNode;
    onClear?: () => void;
}

export const UptimeChecklist = ({
    query,
    onQueryChange,
    placeholder,
    items,
    selected,
    onToggle,
    emptyText,
    hint,
    maxHeight,
    clearLabel,
    onClear,
}: IUptimeChecklistProps) => (
    <>
        <input
            type='text'
            className='upt-finput'
            value={query}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={event => onQueryChange(event.target.value)}
        />
        {onClear && clearLabel && selected.length > 0 && (
            <div className='upt-checklist__actions'>
                <button type='button' className='upt-linkbtn' onClick={onClear}>
                    {clearLabel}
                </button>
            </div>
        )}
        <div className='upt-checklist' style={maxHeight ? { maxHeight } : undefined}>
            {items.length ? (
                items.map(item => (
                    <label key={item.key}>
                        <input
                            type='checkbox'
                            checked={selected.includes(item.key)}
                            onChange={() => onToggle(item.key)}
                        />
                        {item.label}
                        {item.count !== undefined && <em>{item.count}</em>}
                    </label>
                ))
            ) : (
                <p className='upt-checklist__empty'>{emptyText}</p>
            )}
        </div>
        {hint && <small className='upt-fhint'>{hint}</small>}
    </>
);

export interface IUptimeDateRangeProps {
    from: string;
    to: string;
    fromLabel: string;
    toLabel: string;
    onChange: (range: { from: string; to: string }) => void;
}

export const UptimeDateRange = ({ from, to, fromLabel, toLabel, onChange }: IUptimeDateRangeProps) => (
    <div className='upt-daterange'>
        <input
            type='date'
            className='upt-finput'
            value={from}
            max={to || undefined}
            aria-label={fromLabel}
            onChange={event => {
                const next = event.target.value;
                onChange({ from: next, to: next && to && next > to ? next : to });
            }}
        />
        <input
            type='date'
            className='upt-finput'
            value={to}
            min={from || undefined}
            aria-label={toLabel}
            onChange={event => {
                const next = event.target.value;
                onChange({ from: next && from && next < from ? next : from, to: next });
            }}
        />
    </div>
);
